import type { ReviewStatus } from 'store/useReviewStore';
import {
  type MappingFlagged,
  mappingRowStatus,
  pendingMappingProposals,
  pickMappingProposal,
} from './mappingReferenceStatus';

export type MappingReviewAction = 'accept' | 'dismiss' | 'reopen';

const STATUS_BY_ACTION: Record<MappingReviewAction, 'accepted' | 'dismissed' | 'pending'> = {
  accept: 'accepted',
  dismiss: 'dismissed',
  reopen: 'pending',
};

export const mappingReviewStatusOf = (action: MappingReviewAction): 'accepted' | 'dismissed' | 'pending' =>
  STATUS_BY_ACTION[action];

/** A card whose thumbs settle its AI Excel mappings rather than a field or a citation. */
export const isMappingSettleItem = (
  item: { canSettle?: boolean; canSettleCitation?: boolean; canSettleMapping?: boolean }
): boolean => Boolean(item.canSettleMapping && !item.canSettle && !item.canSettleCitation);

// A mapping row as the cell holds it, or as the generated security type spells it.
type MappingRef = { id?: number | null } | string | null;

/** Stamp the settled status on the targeted rows; untouched lists keep their identity. */
export const applyMappingReviewToRefs = <T extends MappingRef>(
  refs: T[] | undefined,
  mappingIds: number[],
  action: MappingReviewAction
): T[] | undefined => {
  if (!Array.isArray(refs)) {
    return refs;
  }
  const targets = new Set(mappingIds);
  const status = mappingReviewStatusOf(action);
  // Stamp the decision time so the latest decision owns the card before the next fetch.
  const updatedAt = new Date().toISOString();
  let changed = false;
  const next = refs.map(ref => {
    if (typeof ref !== 'object' || ref?.id == null || !targets.has(ref.id)) {
      return ref;
    }
    changed = true;
    return { ...ref, status, updated_at: updatedAt };
  });
  return changed ? next : refs;
};

export type MappingRow = MappingFlagged & { id?: number | null };

type MappingCell<T extends MappingRow = MappingRow> = {
  mappingReferences?: T[];
  dispatchEvent?: () => void;
};

/** The review card fields that name the proposal it rules on and which half of the card the thumbs settle. */
type MappingCard<T> = {
  id: string;
  canSettle?: boolean;
  canSettleCitation?: boolean;
  canSettleMapping?: boolean;
  primaryMappingReference?: T;
  mappingIds?: number[];
  pendingProposalCount?: number;
};

type ReviewCardStore<C> = { reviewItems: C[]; setReviewItems: (items: C[]) => void };

// Rewrite one store card in place.
const patchReviewCard = <T, C extends MappingCard<T>>(
  getReviewStore: () => ReviewCardStore<C>,
  itemId: string,
  fields: Partial<MappingCard<T>>
): void => {
  const { reviewItems, setReviewItems } = getReviewStore();
  setReviewItems(reviewItems.map(card => (card.id === itemId ? { ...card, ...fields } : card)));
};

// The card fields that point at the proposal it rules on.
const cardOnProposal = <T extends MappingRow>(proposal: T, refs: T[] | undefined) => ({
  primaryMappingReference: proposal,
  mappingIds: proposal.id != null ? [proposal.id] : [],
  pendingProposalCount: pendingMappingProposals(refs).length,
});

/**
 * Hand a card whose value or citation is decided to the Excel proposal still waiting on its cell:
 * the store card becomes an Excel card ruling on that proposal and the caller keeps it `pending`.
 * Undefined when nothing waits.
 */
export const holdCardOnPendingProposal = <T extends MappingRow, C extends MappingCard<T>>(
  itemId: string,
  refs: T[] | undefined,
  getReviewStore: () => ReviewCardStore<C>
): 'pending' | undefined => {
  const [proposal] = pendingMappingProposals(refs);
  if (!proposal) {
    return undefined;
  }
  patchReviewCard(getReviewStore, itemId, {
    canSettle: false,
    canSettleCitation: false,
    canSettleMapping: true,
    ...cardOnProposal(proposal, refs),
  });
  return 'pending';
};

type MappingSecurity = {
  mapping_references?: MappingRef[];
};

export const applyMappingReviewToCell = (
  cell: MappingCell | null | undefined,
  mappingIds: number[],
  action: MappingReviewAction
) => {
  if (!cell || !Array.isArray(cell.mappingReferences)) {
    return;
  }
  // The spreadsheet already holds this cell object; write the settled rows onto it.
  // eslint-disable-next-line no-param-reassign
  cell.mappingReferences = applyMappingReviewToRefs(cell.mappingReferences, mappingIds, action);
  cell.dispatchEvent?.();
};

/** Stamp the settled rows on the store securities in place: a replaced store rebuilds the grid and drops its unsaved references. */
export const applyMappingReviewToSecurities = (
  securities: MappingSecurity[] | undefined,
  mappingIds: number[],
  action: MappingReviewAction
): void => {
  securities?.forEach(security => {
    const refs = applyMappingReviewToRefs(security.mapping_references, mappingIds, action);
    if (refs !== security.mapping_references) {
      // eslint-disable-next-line no-param-reassign
      security.mapping_references = refs;
    }
  });
};

type SettleMappingReviewParams<T extends MappingRow, C extends MappingCard<T>> = {
  itemId: string;
  mappingIds: number[];
  action: MappingReviewAction;
  persist: (mappingIds: number[], action: MappingReviewAction) => Promise<unknown>;
  cell?: MappingCell<T> | null;
  getSecurities: () => MappingSecurity[] | undefined;
  getReviewStore: () => ReviewCardStore<C>;
};

/** Persist a settle of the card's Excel proposal, mirror it in place on the cell, the securities and the card, and resolve the card's next status. */
export const settleMappingReview = async <T extends MappingRow, C extends MappingCard<T>>({
  itemId,
  mappingIds,
  action,
  persist,
  cell,
  getSecurities,
  getReviewStore,
}: SettleMappingReviewParams<T, C>): Promise<ReviewStatus> => {
  await persist(mappingIds, action);
  applyMappingReviewToCell(cell, mappingIds, action);
  applyMappingReviewToSecurities(getSecurities(), mappingIds, action);
  const refs = cell?.mappingReferences;
  const next = pickMappingProposal(refs);
  if (!next) {
    return mappingReviewStatusOf(action);
  }
  // The card moves to the cell's next proposal now, so a click before the grid re-parses rules on it.
  patchReviewCard(getReviewStore, itemId, cardOnProposal(next, refs));
  return mappingRowStatus(next);
};
